<template>
  <div class="calendar-page">
    <div class="calendar-toolbar">
      <div>
        <div class="calendar-title">我的日历</div>
        <div class="calendar-subtitle">{{ currentRangeLabel }}</div>
        <div class="mode-jump-list">
          <button
            v-for="mode in viewModes"
            :key="mode.value"
            type="button"
            :class="['mode-jump-button', { active: viewMode === mode.value }]"
            @click="jumpToMode(mode.value)"
          >
            {{ mode.label }}
          </button>
        </div>
      </div>
      <div class="calendar-actions">
        <el-button size="small" icon="el-icon-arrow-left" @click="moveDate(-1)"></el-button>
        <el-button size="small" @click="goToday">今天</el-button>
        <el-button size="small" icon="el-icon-arrow-right" @click="moveDate(1)"></el-button>
        <el-radio-group v-model="viewMode" size="small" @change="jumpToMode">
          <el-radio-button v-for="mode in viewModes" :key="mode.value" :label="mode.value">{{ mode.label }}</el-radio-button>
        </el-radio-group>
        <el-button size="small" icon="el-icon-check" @click="openTodoDialog()">新建TODO</el-button>
        <el-button type="primary" size="small" icon="el-icon-plus" @click="openEventDialog()">新建日程</el-button>
      </div>
    </div>

    <div class="calendar-layout">
      <div class="calendar-main" @wheel="handleCalendarWheel">
        <div v-if="viewMode === 'month'" class="month-view">
          <div v-for="day in weekDays" :key="day" class="week-head">{{ day }}</div>
          <div
            v-for="day in monthDays"
            :key="day.key"
            :class="['month-cell', { muted: !day.inMonth, today: day.isToday, selected: day.date === selectedDate }]"
            @click="selectDate(day.date)"
            @dblclick="openEventDialog(null, day.date)"
          >
            <div class="cell-day-row">
              <div class="cell-day">{{ day.day }}</div>
              <span
                v-if="day.dayMode"
                class="day-mode-badge"
                :style="{ color: day.dayMode.color, borderColor: day.dayMode.color, backgroundColor: day.dayMode.background }"
              >
                {{ day.dayMode.label }}
              </span>
            </div>
            <div class="cell-events">
              <div
                v-for="event in day.events.slice(0, 3)"
                :key="event.id"
                class="event-chip"
                :style="{ borderLeftColor: event.color }"
                @click.stop="openEventDialog(event)"
              >
                <div v-if="eventTimeLabel(event)" class="event-chip-time">{{ eventTimeLabel(event) }}</div>
                <div class="event-chip-main">
                  <span class="event-category" :style="{ color: event.color, borderColor: event.color }">{{ event.category }}</span>
                  <span class="event-title">{{ event.title }}</span>
                  <span v-if="eventParticipantCount(event)" class="event-participants">+{{ eventParticipantCount(event) }}</span>
                </div>
              </div>
              <div v-if="day.events.length > 3" class="more-events">+{{ day.events.length - 3 }} 更多</div>
            </div>
          </div>
        </div>

        <div v-else-if="viewMode === 'week'" class="week-view">
          <div class="time-column"></div>
          <div
            v-for="day in visibleWeekDays"
            :key="day.date"
            :class="['day-column-head', { today: day.isToday }]"
            @click="selectDate(day.date)"
          >
            <span>{{ day.week }}</span>
            <strong>{{ day.day }}</strong>
            <em
              v-if="day.dayMode"
              class="day-mode-badge week-mode-badge"
              :style="{ color: day.dayMode.color, borderColor: day.dayMode.color, backgroundColor: day.dayMode.background }"
            >
              {{ day.dayMode.label }}
            </em>
          </div>
          <div class="all-day-label">全天</div>
          <div
            v-for="day in visibleWeekDays"
            :key="day.date + '-all-day'"
            class="all-day-slot"
            @dblclick="openEventDialog(null, day.date)"
          >
            <div
              v-for="event in allDayEventsForDate(day.date)"
              :key="event.id"
              class="week-event all-day-event"
              :style="{ backgroundColor: event.color }"
              @click.stop="openEventDialog(event)"
              @dblclick.stop
            >
              {{ event.title }}
              <span v-if="eventParticipantCount(event)"> +{{ eventParticipantCount(event) }}</span>
            </div>
          </div>
          <template v-for="hour in dayHours">
            <div :key="'time-' + hour" class="time-label">{{ hour }}:00</div>
            <div
              v-for="day in visibleWeekDays"
              :key="day.date + '-' + hour"
              class="time-slot"
              @dblclick="openEventDialog(null, day.date, padHour(hour))"
            >
              <div
                v-for="event in eventsForHour(day.date, hour)"
                :key="event.id"
                class="week-event"
                :style="{ backgroundColor: event.color }"
                @click.stop="openEventDialog(event)"
              >
                {{ eventTimeLabel(event) }} {{ event.title }}
                <span v-if="eventParticipantCount(event)"> +{{ eventParticipantCount(event) }}</span>
              </div>
            </div>
          </template>
        </div>

        <div v-else-if="viewMode === 'day'" class="day-view">
          <div class="day-all-day-row">
            <div class="day-time-label">全天</div>
            <div class="day-time-events all-day-list" @dblclick="openEventDialog(null, selectedDate)">
              <div
                v-for="event in allDayEventsForDate(selectedDate)"
                :key="event.id"
                class="day-event all-day-day-event"
                :style="{ borderLeftColor: event.color }"
                @click="openEventDialog(event)"
                @dblclick.stop
              >
                <strong>{{ event.title }}</strong>
                <span v-if="eventTimeLabel(event)">{{ eventTimeLabel(event) }}</span>
                <span v-if="eventParticipantNames(event)">参与人：{{ eventParticipantNames(event) }}</span>
                <p v-if="event.notes">{{ event.notes }}</p>
              </div>
            </div>
          </div>
          <div
            v-for="hour in dayHours"
            :key="hour"
            class="day-time-row"
            @dblclick="openEventDialog(null, selectedDate, padHour(hour))"
          >
            <div class="day-time-label">{{ hour }}:00</div>
            <div class="day-time-events">
              <div
                v-for="event in eventsForHour(selectedDate, hour)"
                :key="event.id"
                class="day-event"
                :style="{ borderLeftColor: event.color }"
                @click="openEventDialog(event)"
              >
                <strong>{{ event.title }}</strong>
                <span>{{ eventTimeLabel(event) }}</span>
                <span v-if="eventParticipantNames(event)">参与人：{{ eventParticipantNames(event) }}</span>
                <p v-if="event.notes">{{ event.notes }}</p>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="list-view">
          <div v-for="group in upcomingGroups" :key="group.date" class="list-group">
            <div class="list-date">{{ formatDisplayDate(group.date) }}</div>
            <div
              v-for="event in group.events"
              :key="event.id"
              class="list-event"
              @click="openEventDialog(event)"
            >
              <span class="dot" :style="{ backgroundColor: event.color }"></span>
              <div>
                <strong>{{ event.title }}</strong>
                <p v-if="eventTimeLabel(event) || event.location">
                  <span v-if="eventTimeLabel(event)">{{ eventTimeLabel(event) }}</span>
                  <span v-if="event.location">{{ eventTimeLabel(event) ? ' · ' : '' }}{{ event.location }}</span>
                </p>
                <p v-if="eventParticipantNames(event)">参与人：{{ eventParticipantNames(event) }}</p>
              </div>
            </div>
          </div>
          <el-empty v-if="upcomingGroups.length === 0" description="暂无日程"></el-empty>
        </div>
      </div>

      <div class="calendar-side">
        <div class="side-card">
          <div>
            <div class="side-title">{{ formatDisplayDate(selectedDate) }}</div>
            <el-select
              :value="selectedDayModeValue"
              size="mini"
              clearable
              placeholder="标记工作日模式"
              class="day-mode-select"
              @change="saveSelectedDayMode"
            >
              <el-option
                v-for="mode in workdayModes"
                :key="mode.value"
                :label="mode.label"
                :value="mode.value"
              ></el-option>
            </el-select>
          </div>
          <div class="side-actions">
            <el-button type="text" icon="el-icon-check" @click="openTodoDialog(selectedDate)">TODO</el-button>
            <el-button type="text" icon="el-icon-plus" @click="openEventDialog(null, selectedDate)">日程</el-button>
          </div>
        </div>
        <div v-for="event in selectedDateEvents" :key="event.id" class="side-event" @click="openEventDialog(event)">
          <div class="side-event-color" :style="{ backgroundColor: event.color }"></div>
          <div class="side-event-content">
            <p v-if="eventTimeLabel(event)" class="side-event-time">{{ eventTimeLabel(event) }}</p>
            <div class="side-event-main">
              <span class="event-category" :style="{ color: event.color, borderColor: event.color }">{{ event.category }}</span>
              <strong>{{ event.title }}</strong>
            </div>
            <p v-if="event.location">{{ event.location }}</p>
            <p v-if="eventParticipantNames(event)">参与人：{{ eventParticipantNames(event) }}</p>
          </div>
        </div>
        <el-empty v-if="selectedDateEvents.length === 0" description="当天暂无日程"></el-empty>
      </div>
    </div>

    <el-dialog :title="eventDialogTitle" :visible.sync="eventDialogVisible" width="520px">
      <el-alert
        v-if="!canEditEvent"
        :title="readonlyAlertTitle"
        type="info"
        :closable="false"
        show-icon
        class="readonly-alert"
      ></el-alert>
      <el-form :model="eventForm" label-width="82px">
        <el-form-item v-if="isTodoEvent" label="状态">
          <el-checkbox
            v-model="eventForm.finish_status"
            :disabled="todoStatusUpdating"
            @change="toggleCalendarTodoFinish"
          >
            已完成
          </el-checkbox>
        </el-form-item>
        <el-form-item label="标题">
          <el-input v-model="eventForm.title" :disabled="!canEditEvent" placeholder="例如：客户会议"></el-input>
        </el-form-item>
        <el-form-item label="日期">
          <el-date-picker
            v-model="eventForm.date"
            :disabled="!canEditEvent"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="开始日期"
            @change="syncEndDateWithStart"
          ></el-date-picker>
        </el-form-item>
        <el-form-item v-if="isTodoEvent" label="时间类型">
          <el-radio-group v-model="eventForm.time_type" :disabled="!canEditEvent" @change="syncTodoEventTimeType">
            <el-radio label="时间段"></el-radio>
            <el-radio label="截止时间"></el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="时间">
          <el-checkbox v-model="eventForm.allDay" :disabled="!canEditEvent" @change="syncEndDateWithStart">全天</el-checkbox>
          <div v-if="eventForm.allDay && (!isTodoEvent || eventForm.time_type === '时间段')" class="date-range-picker">
            <el-date-picker
              v-model="eventForm.endDate"
              :disabled="!canEditEvent"
              type="date"
              value-format="yyyy-MM-dd"
              :picker-options="endDatePickerOptions"
              placeholder="结束日期"
            ></el-date-picker>
          </div>
          <div v-if="!eventForm.allDay" class="time-pickers">
            <el-time-select
              v-if="!isTodoEvent || eventForm.time_type === '时间段'"
              v-model="eventForm.startTime"
              :picker-options="timeOptions"
              :disabled="!canEditEvent"
              placeholder="开始"
            ></el-time-select>
            <el-time-select
              v-model="eventForm.endTime"
              :picker-options="endTimeOptions"
              :disabled="!canEditEvent"
              :placeholder="isTodoEvent && eventForm.time_type === '截止时间' ? '截止' : '结束'"
            ></el-time-select>
          </div>
        </el-form-item>
        <el-form-item v-if="!isTodoEvent" label="分类">
          <el-select v-model="eventForm.category" :disabled="!canEditEvent" placeholder="选择分类" @change="syncCategoryColor">
            <el-option v-for="item in categories" :key="item.name" :label="item.name" :value="item.name"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item v-if="!isTodoEvent" label="地点">
          <el-input v-model="eventForm.location" :disabled="!canEditEvent" placeholder="可选"></el-input>
        </el-form-item>
        <el-form-item v-if="!isTodoEvent" label="参与人">
          <el-select
            v-model="eventForm.participant_ids"
            multiple
            filterable
            clearable
            collapse-tags
            :disabled="!canEditEvent"
            placeholder="选择同事"
            class="participant-select"
          >
            <el-option
              v-for="user in colleagueOptions"
              :key="user.id"
              :label="user.name"
              :value="user.id"
            >
              <span>{{ user.name }}</span>
              <span v-if="user.department" class="participant-option-meta">{{ user.department }}</span>
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="备注">
          <el-input v-model="eventForm.notes" type="textarea" :rows="3" :disabled="!canEditEvent" placeholder="可选"></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button v-if="editingEventId && canEditEvent" type="danger" plain @click="deleteEvent">删除</el-button>
        <el-button @click="eventDialogVisible = false">取消</el-button>
        <el-button v-if="canEditEvent" type="primary" @click="saveEvent">保存</el-button>
      </span>
    </el-dialog>

    <el-dialog title="快速新建 TODO" :visible.sync="todoDialogVisible" width="520px">
      <el-form :model="todoForm" label-width="82px">
        <el-form-item label="内容">
          <el-input v-model="todoForm.title" placeholder="请输入 TODO 内容"></el-input>
        </el-form-item>
        <el-form-item label="清单">
          <el-select v-model="todoForm.list_id" filterable placeholder="选择 TODO 清单" class="todo-list-select">
            <el-option
              v-for="list in todoListOptions"
              :key="list.id"
              :label="list.name"
              :value="list.id"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="标签">
          <el-select
            v-model="todoForm.todo_labels"
            multiple
            filterable
            allow-create
            default-first-option
            placeholder="输入标签后回车"
            class="todo-list-select"
          ></el-select>
        </el-form-item>
        <el-form-item label="时间类型">
          <el-radio-group v-model="todoForm.time_type">
            <el-radio label="时间段"></el-radio>
            <el-radio label="截止时间"></el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="日期">
          <el-date-picker
            v-model="todoForm.date"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="选择日期"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="时间">
          <div class="time-pickers">
            <el-time-select
              v-if="todoForm.time_type === '时间段'"
              v-model="todoForm.startTime"
              clearable
              :picker-options="timeOptions"
              placeholder="可选开始"
            ></el-time-select>
            <el-time-select
              v-model="todoForm.endTime"
              clearable
              :picker-options="todoEndTimeOptions"
              :placeholder="todoForm.time_type === '时间段' ? '可选结束' : '可选截止'"
            ></el-time-select>
          </div>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="todoDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="saveTodo">保存TODO</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import 'dayjs/locale/zh-cn'
import { getUserId } from '@/utils/store'
import {
  getCalendarEvents,
  getCalendarDayModes,
  getCalendarTODOs,
  getLists,
  getTODO,
  quickCreateCalendarTODO,
  saveCalendarDayMode,
  saveCalendarEvent,
  deleteCalendarEvent,
  finishTODO,
  getUserList,
  unfinishTODO,
  updateTODO,
  updateTODONote,
  deleteTODO,
  netService
} from '@/api/assistant'

const STORAGE_PREFIX = 'assistant_calendar_events_'

dayjs.locale('zh-cn')

function createEmptyEventForm () {
  return {
    title: '',
    date: dayjs().format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    allDay: false,
    startTime: '09:00',
    endTime: '10:00',
    category: '工作',
    color: '#4a8af4',
    location: '',
    participant_ids: [],
    notes: ''
  }
}

function createEmptyTodoForm () {
  return {
    title: '',
    list_id: '',
    todo_labels: [],
    time_type: '时间段',
    date: dayjs().format('YYYY-MM-DD'),
    startTime: '',
    endTime: ''
  }
}

export default {
  name: 'Calendar',
  data () {
    return {
      viewMode: 'month',
      currentDate: dayjs().format('YYYY-MM-DD'),
      selectedDate: dayjs().format('YYYY-MM-DD'),
      events: [],
      todoEvents: [],
      dayModes: {},
      backendAvailable: true,
      eventDialogVisible: false,
      editingEventId: '',
      eventForm: createEmptyEventForm(),
      todoDialogVisible: false,
      todoForm: createEmptyTodoForm(),
      todoStatusUpdating: false,
      todoLists: [],
      colleagues: [],
      monthWheelGestureActive: false,
      monthWheelUnlockTimer: null,
      todoLoadTimer: null,
      dayModeLoadTimer: null,
      viewModes: [
        { value: 'month', label: '月' },
        { value: 'week', label: '周' },
        { value: 'day', label: '日' },
        { value: 'list', label: '列表' }
      ],
      weekDays: ['周一', '周二', '周三', '周四', '周五', '周六', '周日'],
      dayHours: Array.from({ length: 18 }, (item, index) => index + 6),
      workdayModes: [
        { value: 'depth', label: 'Depth', fullLabel: 'Depth Mode', color: '#7c5cff', background: '#f3efff' },
        { value: 'breadth', label: 'Breadth', fullLabel: 'Breadth Mode', color: '#00a870', background: '#eaf8f1' },
        { value: 'priority_split', label: 'Priority', fullLabel: 'Priority Split Mode', color: '#f59f00', background: '#fff6e6' }
      ],
      categories: [
        { name: '工作', color: '#4a8af4' },
        { name: '个人', color: '#28a745' },
        { name: '会议', color: '#f59f00' },
        { name: '重要', color: '#dd4f43' },
        { name: '计划', color: '#7c5cff' },
        { name: '运动', color: '#00a870' },
        { name: '休息', color: '#6c8ea4' },
        { name: '娱乐', color: '#e85aad' }
      ],
      timeOptions: {
        start: '06:00',
        step: '00:30',
        end: '24:00'
      }
    }
  },
  computed: {
    userId () {
      return getUserId() || 'guest'
    },
    storageKey () {
      return STORAGE_PREFIX + this.userId
    },
    monthDays () {
      const start = dayjs(this.currentDate).startOf('month')
      const end = dayjs(this.currentDate).endOf('month')
      const startOffset = (start.day() + 6) % 7
      const gridStart = start.subtract(startOffset, 'day')
      const totalDays = Math.ceil((startOffset + end.date()) / 7) * 7

      return Array.from({ length: totalDays }, (item, index) => {
        const date = gridStart.add(index, 'day')
        const value = date.format('YYYY-MM-DD')
        return {
          key: value,
          date: value,
          day: date.date(),
          inMonth: date.month() === start.month(),
          isToday: value === dayjs().format('YYYY-MM-DD'),
          dayMode: this.dayModeForDate(value),
          events: this.eventsForDate(value)
        }
      })
    },
    visibleRange () {
      if (this.viewMode === 'month') {
        const start = dayjs(this.currentDate).startOf('month')
        const end = dayjs(this.currentDate).endOf('month')
        const startOffset = (start.day() + 6) % 7
        const gridStart = start.subtract(startOffset, 'day')
        const totalDays = Math.ceil((startOffset + end.date()) / 7) * 7
        return {
          start: gridStart.format('YYYY-MM-DD'),
          end: gridStart.add(totalDays - 1, 'day').format('YYYY-MM-DD')
        }
      }
      if (this.viewMode === 'week') {
        const start = this.weekStart(dayjs(this.currentDate))
        return {
          start: start.format('YYYY-MM-DD'),
          end: start.add(6, 'day').format('YYYY-MM-DD')
        }
      }
      if (this.viewMode === 'day') {
        return {
          start: this.selectedDate,
          end: this.selectedDate
        }
      }
      const start = dayjs().startOf('day')
      return {
        start: start.format('YYYY-MM-DD'),
        end: start.add(90, 'day').format('YYYY-MM-DD')
      }
    },
    calendarItems () {
      return this.events.concat(this.todoEvents)
    },
    visibleWeekDays () {
      const start = this.weekStart(dayjs(this.currentDate))
      return Array.from({ length: 7 }, (item, index) => {
        const date = start.add(index, 'day')
        return {
          date: date.format('YYYY-MM-DD'),
          week: this.weekDays[index],
          day: date.format('MM-DD'),
          isToday: date.isSame(dayjs(), 'day'),
          dayMode: this.dayModeForDate(date.format('YYYY-MM-DD'))
        }
      })
    },
    selectedDateEvents () {
      return this.eventsForDate(this.selectedDate)
    },
    selectedDayModeValue () {
      const dayMode = this.dayModes[this.selectedDate]
      return dayMode ? dayMode.mode : ''
    },
    currentRangeLabel () {
      if (this.viewMode === 'month') return dayjs(this.currentDate).format('YYYY年M月')
      if (this.viewMode === 'week') {
        const start = this.weekStart(dayjs(this.currentDate))
        return `${start.format('YYYY年M月D日')} - ${start.add(6, 'day').format('M月D日')}`
      }
      if (this.viewMode === 'day') return dayjs(this.selectedDate).format('YYYY年M月D日')
      return '未来日程'
    },
    upcomingGroups () {
      const today = dayjs().startOf('day')
      const grouped = this.calendarItems.reduce((groups, event) => {
        this.eventDates(event).forEach(date => {
          if (dayjs(date).isBefore(today, 'day')) return
          if (!groups[date]) groups[date] = []
          groups[date].push(event)
        })
        return groups
      }, {})

      return Object.keys(grouped).sort().map(date => ({
        date,
        events: grouped[date].sort(this.sortEvents)
      }))
    },
    endTimeOptions () {
      if (this.isTodoEvent && this.eventForm.time_type === '截止时间') {
        return {
          ...this.timeOptions
        }
      }
      return {
        ...this.timeOptions,
        minTime: this.eventForm.startTime || ''
      }
    },
    todoEndTimeOptions () {
      return {
        ...this.timeOptions,
        minTime: this.todoForm.time_type === '时间段' ? this.todoForm.startTime || '' : ''
      }
    },
    endDatePickerOptions () {
      return {
        disabledDate: date => {
          if (!this.eventForm.date) return false
          return dayjs(date).isBefore(dayjs(this.eventForm.date), 'day')
        }
      }
    },
    colleagueOptions () {
      return this.colleagues.filter(user => user.id !== this.userId)
    },
    todoListOptions () {
      return this.todoLists.map(list => ({
        id: this.normalizeUserId(list._id || list.id),
        name: list.list_name || list.name || '未命名清单'
      })).filter(list => list.id)
    },
    isTodoEvent () {
      return this.eventForm.source_type === 'todo'
    },
    canEditEvent () {
      if (this.eventForm.source_type === 'todo') return true
      if (this.eventForm.readonly) return false
      return !this.eventForm.user_id || this.eventForm.user_id === this.userId
    },
    eventDialogTitle () {
      if (!this.editingEventId) return '新建日程'
      if (this.eventForm.source_type === 'todo') return this.canEditEvent ? '编辑 TODO' : 'TODO 详情'
      return this.canEditEvent ? '编辑日程' : '日程详情'
    },
    readonlyAlertTitle () {
      if (this.eventForm.source_type === 'todo') return '这是事项清单同步过来的 TODO，请回到事项清单中编辑'
      return '这是同事邀请你参与的日程，仅创建人可编辑'
    }
  },
  watch: {
    visibleRange () {
      if (this.todoLoadTimer) clearTimeout(this.todoLoadTimer)
      this.todoLoadTimer = setTimeout(() => {
        this.todoLoadTimer = null
        this.loadTodoEvents()
      }, 120)
      if (this.dayModeLoadTimer) clearTimeout(this.dayModeLoadTimer)
      this.dayModeLoadTimer = setTimeout(() => {
        this.dayModeLoadTimer = null
        this.loadDayModes()
      }, 120)
    }
  },
  async mounted () {
    await Promise.all([
      this.loadEvents(),
      this.loadColleagues(),
      this.loadTodoLists(),
      this.loadTodoEvents(),
      this.loadDayModes()
    ])
  },
  beforeDestroy () {
    if (this.monthWheelUnlockTimer) clearTimeout(this.monthWheelUnlockTimer)
    if (this.todoLoadTimer) clearTimeout(this.todoLoadTimer)
    if (this.dayModeLoadTimer) clearTimeout(this.dayModeLoadTimer)
  },
  methods: {
    normalizeUserId (value) {
      if (!value) return ''
      if (typeof value === 'string') return value
      if (value.$id) return value.$id
      if (value.oid) return value.oid
      return String(value)
    },
    normalizeUserName (user) {
      const fullName = `${user.user_lastName || ''}${user.user_givenName || ''}`.trim()
      return fullName || user.user_name || user.employee_name || user.user_email || '未命名同事'
    },
    async loadColleagues () {
      try {
        const res = await getUserList()
        const users = Array.isArray(res && res.data) ? res.data : []
        this.colleagues = users.map(user => ({
          id: this.normalizeUserId(user._id || user.id),
          name: this.normalizeUserName(user),
          department: user.employee_department || ''
        })).filter(user => user.id)
      } catch (error) {
        this.colleagues = []
      }
    },
    async loadTodoLists () {
      try {
        const res = await getLists(this.userId, 'user')
        this.todoLists = Array.isArray(res && res.data) ? res.data : []
      } catch (error) {
        this.todoLists = []
      }
    },
    loadLocalEvents () {
      try {
        return JSON.parse(localStorage.getItem(this.storageKey) || '[]')
      } catch (error) {
        return []
      }
    },
    async loadEvents () {
      const localEvents = this.loadLocalEvents()

      try {
        const res = await getCalendarEvents(this.userId)
        const data = res && res.data
        if (data && data.status === 'fail') throw new Error(data.msg || 'Calendar backend load failed')
        const events = Array.isArray(data) ? data : data && Array.isArray(data.data) ? data.data : null
        if (events) {
          this.events = events.map(this.normalizeEvent)
          this.persistEvents()
          return
        }
      } catch (error) {
        this.backendAvailable = false
      }

      this.events = localEvents.map(this.normalizeEvent)
    },
    async loadTodoEvents () {
      if (this.userId === 'guest') {
        this.todoEvents = []
        return
      }

      try {
        const { start, end } = this.visibleRange
        const res = await getCalendarTODOs(this.userId, start, end)
        const data = res && res.data
        if (data && data.status === 'fail') throw new Error(data.msg || 'TODO calendar load failed')
        const todos = Array.isArray(data) ? data : data && Array.isArray(data.data) ? data.data : []
        this.todoEvents = todos.map(this.normalizeEvent)
      } catch (error) {
        this.todoEvents = []
      }
    },
    async loadDayModes () {
      if (this.userId === 'guest') {
        this.dayModes = {}
        return
      }

      try {
        const { start, end } = this.visibleRange
        const res = await getCalendarDayModes(this.userId, start, end)
        const data = res && res.data
        if (data && data.status === 'fail') throw new Error(data.msg || 'Calendar day mode load failed')
        const dayModes = Array.isArray(data) ? data : data && Array.isArray(data.data) ? data.data : []
        this.dayModes = dayModes.reduce((result, item) => {
          const option = this.workdayModes.find(mode => mode.value === item.mode)
          if (item.date && option) result[item.date] = { ...option, mode: option.value }
          return result
        }, {})
      } catch (error) {
        this.dayModes = {}
      }
    },
    persistEvents () {
      localStorage.setItem(this.storageKey, JSON.stringify(this.events))
    },
    weekStart (date) {
      return date.subtract((date.day() + 6) % 7, 'day')
    },
    selectDate (date) {
      this.selectedDate = date
      this.currentDate = date
    },
    moveDate (step) {
      const unit = this.viewMode === 'month' ? 'month' : this.viewMode === 'week' ? 'week' : 'day'
      this.currentDate = dayjs(this.currentDate).add(step, unit).format('YYYY-MM-DD')
      if (this.viewMode === 'day') this.selectedDate = this.currentDate
    },
    jumpToMode (mode) {
      this.viewMode = mode
      if (mode === 'day') {
        this.currentDate = this.selectedDate
      } else if (mode === 'week' || mode === 'month') {
        this.currentDate = this.selectedDate || this.currentDate
      }
    },
    switchMonthByWheel (event) {
      const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX
      if (!delta) return

      event.preventDefault()
      if (this.monthWheelUnlockTimer) clearTimeout(this.monthWheelUnlockTimer)
      this.monthWheelUnlockTimer = setTimeout(() => {
        this.monthWheelGestureActive = false
        this.monthWheelUnlockTimer = null
      }, 100)

      if (this.monthWheelGestureActive) return

      this.monthWheelGestureActive = true
      this.moveDate(delta > 0 ? 1 : -1)
    },
    handleCalendarWheel (event) {
      if (this.viewMode !== 'month') return
      this.switchMonthByWheel(event)
    },
    goToday () {
      this.currentDate = dayjs().format('YYYY-MM-DD')
      this.selectedDate = this.currentDate
    },
    syncEndDateWithStart () {
      if (!this.eventForm.date) return
      if (this.eventForm.source_type === 'todo' && this.eventForm.time_type === '截止时间') {
        this.eventForm.endDate = this.eventForm.date
        return
      }
      if (!this.eventForm.endDate || dayjs(this.eventForm.endDate).isBefore(dayjs(this.eventForm.date), 'day')) {
        this.eventForm.endDate = this.eventForm.date
      }
    },
    syncTodoEventTimeType () {
      if (this.eventForm.time_type === '截止时间') {
        this.eventForm.startTime = ''
        this.eventForm.endDate = this.eventForm.date
      }
      this.syncEndDateWithStart()
    },
    padHour (hour) {
      return `${String(hour).padStart(2, '0')}:00`
    },
    openEventDialog (event, date, time) {
      if (event) {
        this.editingEventId = event.id
        this.eventForm = {
          ...createEmptyEventForm(),
          ...event,
          participant_ids: Array.isArray(event.participant_ids) ? event.participant_ids : []
        }
      } else {
        this.editingEventId = ''
        const eventDate = date || this.selectedDate
        this.eventForm = {
          ...createEmptyEventForm(),
          date: eventDate,
          endDate: eventDate,
          startTime: time || '09:00',
          endTime: time ? dayjs(`2000-01-01 ${time}`).add(1, 'hour').format('HH:mm') : '10:00'
        }
      }
      this.eventDialogVisible = true
    },
    openTodoDialog (date, time) {
      const todoDate = date || this.selectedDate || dayjs().format('YYYY-MM-DD')
      const startTime = time || ''
      this.todoForm = {
        ...createEmptyTodoForm(),
        date: todoDate,
        startTime,
        endTime: startTime ? this.nextTimeOption(startTime) : '',
        list_id: this.todoListOptions.length > 0 ? this.todoListOptions[0].id : ''
      }
      if (this.todoLists.length === 0) this.loadTodoLists()
      this.todoDialogVisible = true
    },
    nextTimeOption (time) {
      if (!time) return '10:00'
      const [hourValue, minuteValue] = time.split(':').map(Number)
      const minutes = (hourValue * 60) + minuteValue + 60
      if (minutes >= 24 * 60) return '24:00'
      return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`
    },
    dayModeForDate (date) {
      const dayMode = this.dayModes[date]
      if (!dayMode) return null
      return this.workdayModes.find(mode => mode.value === dayMode.mode) || null
    },
    async saveSelectedDayMode (mode) {
      if (this.userId === 'guest') {
        this.$message.error('请先登录')
        return
      }

      const previousModes = { ...this.dayModes }
      const option = this.workdayModes.find(item => item.value === mode)
      if (option) {
        this.dayModes = {
          ...this.dayModes,
          [this.selectedDate]: { ...option, mode: option.value }
        }
      } else {
        const nextModes = { ...this.dayModes }
        delete nextModes[this.selectedDate]
        this.dayModes = nextModes
      }

      try {
        const res = await saveCalendarDayMode(this.userId, this.selectedDate, mode || '')
        const data = res && res.data
        if (data && data.status === 'fail') throw new Error(data.msg || '工作日模式保存失败')
        this.$message.success(mode ? '工作日模式已标记' : '工作日模式已清除')
      } catch (error) {
        this.dayModes = previousModes
        this.$message.error(error.message || '工作日模式保存失败')
      }
    },
    todoIdFromEvent (event) {
      const candidates = [event.todo_id, event.todoId, event.todoID, event._id, event.id]
      for (const candidate of candidates) {
        const id = this.normalizeUserId(candidate)
        if (id) return id.replace(/^todo[-_:]/i, '')
      }
      return ''
    },
    isEventTodoFinished (event) {
      const status = event && event.finish_status
      return status === true || status === 1 || status === '1' || status === 'true'
    },
    ensureBackendSuccess (response, fallbackMessage) {
      const data = response && response.data ? response.data : response
      if (data && (data.status === 'fail' || data.status === 'error' || Number(data.code) < 0)) {
        throw new Error(data.msg || fallbackMessage)
      }
      return data
    },
    async toggleCalendarTodoFinish (checked) {
      if (!this.isTodoEvent || this.todoStatusUpdating) return
      const todoId = this.todoIdFromEvent(this.eventForm)
      if (!todoId) {
        this.eventForm.finish_status = !checked
        this.$message.error('TODO 数据缺少 ID，无法修改状态')
        return
      }

      const nextStatus = Boolean(checked)
      const previousStatus = !nextStatus
      this.todoStatusUpdating = true
      this.eventForm.finish_status = nextStatus

      try {
        const response = nextStatus
          ? await finishTODO(todoId, this.userId)
          : await unfinishTODO(todoId, this.userId)
        this.ensureBackendSuccess(response, nextStatus ? 'TODO 标记完成失败' : 'TODO 取消完成失败')
        this.todoEvents = this.todoEvents.map(event => {
          if (this.todoIdFromEvent(event) !== todoId) return event
          return {
            ...event,
            finish_status: nextStatus
          }
        })
        await this.loadTodoEvents()
        this.$message.success(nextStatus ? 'TODO 已完成' : 'TODO 已恢复未完成')
      } catch (error) {
        this.eventForm.finish_status = previousStatus
        this.todoEvents = this.todoEvents.map(event => {
          if (this.todoIdFromEvent(event) !== todoId) return event
          return {
            ...event,
            finish_status: previousStatus
          }
        })
        this.$message.error(error.message || (nextStatus ? 'TODO 标记完成失败' : 'TODO 取消完成失败'))
      } finally {
        this.todoStatusUpdating = false
      }
    },
    async loadOriginalTODO (todoId) {
      try {
        const response = await getTODO(todoId)
        const data = this.ensureBackendSuccess(response, 'TODO 读取失败')
        return data && data.data ? data.data : data || {}
      } catch (error) {
        return {}
      }
    },
    buildTodoDateTimesFromEventForm () {
      const timeType = this.eventForm.time_type || '截止时间'
      const date = this.eventForm.date
      const endDate = this.eventForm.endDate || date

      if (this.eventForm.allDay) {
        return {
          timeType,
          startTime: timeType === '时间段' ? date : '',
          endTime: timeType === '时间段' ? endDate : date
        }
      }

      if (timeType === '时间段') {
        return {
          timeType,
          startTime: `${date} ${this.eventForm.startTime}`,
          endTime: `${endDate} ${this.eventForm.endTime}`
        }
      }

      return {
        timeType,
        startTime: '',
        endTime: `${date} ${this.eventForm.endTime}`
      }
    },
    async saveCalendarTodoEvent () {
      const title = this.eventForm.title.trim()
      if (!title) {
        this.$message.error('请输入 TODO 内容')
        return
      }
      if (!this.eventForm.date) {
        this.$message.error('请选择 TODO 日期')
        return
      }

      const todoId = this.todoIdFromEvent(this.eventForm)
      if (!todoId) {
        this.$message.error('TODO 数据缺少 ID，无法保存')
        return
      }

      const timeType = this.eventForm.time_type || '截止时间'
      if (timeType === '时间段' && this.eventForm.allDay && dayjs(this.eventForm.endDate).isBefore(dayjs(this.eventForm.date), 'day')) {
        this.$message.error('结束日期不能早于开始日期')
        return
      }
      if (!this.eventForm.allDay && timeType === '时间段' && (!this.eventForm.startTime || !this.eventForm.endTime)) {
        this.$message.error('请选择开始和结束时间')
        return
      }
      if (!this.eventForm.allDay && timeType === '截止时间' && !this.eventForm.endTime) {
        this.$message.error('请选择截止时间')
        return
      }

      try {
        const originalTodo = await this.loadOriginalTODO(todoId)
        const todoLabels = Array.isArray(this.eventForm.todo_labels)
          ? this.eventForm.todo_labels
          : Array.isArray(originalTodo.todo_labels) ? originalTodo.todo_labels : []
        const listId = this.eventForm.list_id || originalTodo.list_id || ''
        const todoTimes = this.buildTodoDateTimesFromEventForm()

        this.ensureBackendSuccess(
          await updateTODO(todoId, this.userId, 'user', title, listId, todoLabels),
          'TODO 内容保存失败'
        )
        const todoSaveResponses = await Promise.all([
          updateTODONote(todoId, this.eventForm.notes || ''),
          netService('updateTimeTypeForTODO', {
            todo_id: todoId,
            time_type: todoTimes.timeType
          }),
          netService('updateStartTimeForTODO', {
            todo_id: todoId,
            start_time: todoTimes.startTime,
            end_time: todoTimes.endTime
          })
        ])
        todoSaveResponses.forEach(response => {
          this.ensureBackendSuccess(response, 'TODO 保存失败')
        })

        this.selectedDate = this.eventForm.date
        this.currentDate = this.eventForm.date
        await this.loadTodoEvents()
        this.eventDialogVisible = false
        this.$message.success('TODO 已保存')
      } catch (error) {
        this.$message.error(error.message || 'TODO 保存失败')
      }
    },
    async saveEvent () {
      if (this.eventForm.source_type === 'todo') {
        await this.saveCalendarTodoEvent()
        return
      }
      if (!this.canEditEvent) {
        this.$message.info('当前日程不可编辑')
        return
      }
      if (!this.eventForm.title.trim()) {
        this.$message.error('请输入日程标题')
        return
      }
      if (!this.eventForm.date) {
        this.$message.error('请选择日期')
        return
      }
      this.syncEndDateWithStart()
      if (this.eventForm.allDay && dayjs(this.eventForm.endDate).isBefore(dayjs(this.eventForm.date), 'day')) {
        this.$message.error('结束日期不能早于开始日期')
        return
      }
      if (!this.eventForm.allDay && (!this.eventForm.startTime || !this.eventForm.endTime)) {
        this.$message.error('请选择开始和结束时间')
        return
      }

      const event = {
        ...this.eventForm,
        title: this.eventForm.title.trim(),
        endDate: this.eventForm.allDay ? this.eventForm.endDate : this.eventForm.date,
        participant_ids: Array.isArray(this.eventForm.participant_ids) ? this.eventForm.participant_ids : [],
        user_id: this.eventForm.user_id || this.userId,
        id: this.editingEventId || `${Date.now()}-${Math.random().toString(16).slice(2)}`
      }

      if (this.editingEventId) {
        this.events = this.events.map(item => item.id === this.editingEventId ? event : item)
      } else {
        this.events.push(event)
      }
      this.selectedDate = event.date
      this.currentDate = event.date
      this.persistEvents()

      if (this.backendAvailable) {
        try {
          const res = await saveCalendarEvent(this.userId, event)
          if (res && res.data && res.data.status === 'fail') throw new Error(res.data.msg || 'Calendar backend save failed')
        } catch (error) {
          this.backendAvailable = false
          this.$message.warning('后端日历接口暂不可用，已先保存到本机')
        }
      }

      this.eventDialogVisible = false
      this.$message.success('日程已保存')
    },
    async saveTodo () {
      if (!this.todoForm.title.trim()) {
        this.$message.error('请输入 TODO 内容')
        return
      }
      if (!this.todoForm.list_id) {
        this.$message.error('请选择 TODO 清单')
        return
      }
      if (!this.todoForm.date) {
        this.$message.error('请选择 TODO 日期')
        return
      }

      const startTime = this.todoForm.time_type === '时间段' ? this.todoForm.startTime : ''
      const startDateTime = startTime ? `${this.todoForm.date} ${startTime}` : this.todoForm.date
      const endDateTime = this.todoForm.endTime ? `${this.todoForm.date} ${this.todoForm.endTime}` : this.todoForm.date
      const payload = {
        user_id: this.userId,
        content: this.todoForm.title.trim(),
        list_id: this.todoForm.list_id,
        todo_labels: this.normalizeTodoLabels(this.todoForm.todo_labels),
        time_type: this.todoForm.time_type,
        start_time: this.todoForm.time_type === '时间段' ? startDateTime : '',
        end_time: endDateTime
      }

      try {
        const res = await quickCreateCalendarTODO(payload)
        const data = res && res.data
        if (data && data.status === 'fail') throw new Error(data.msg || 'TODO 保存失败')
        this.todoDialogVisible = false
        this.$message.success('TODO 已创建')
        await this.loadTodoEvents()
      } catch (error) {
        this.$message.error(error.message || 'TODO 保存失败')
      }
    },
    normalizeTodoLabels (labels) {
      const results = []
      const source = Array.isArray(labels) ? labels : []
      source.forEach(label => {
        const value = String(label || '').replace(/^#/, '').trim()
        if (value && !results.includes(value)) results.push(value)
      })
      return results
    },
    async deleteCalendarTodoEvent () {
      const todoId = this.todoIdFromEvent(this.eventForm)
      if (!todoId) {
        this.$message.error('TODO 数据缺少 ID，无法删除')
        return
      }

      try {
        this.ensureBackendSuccess(await deleteTODO(todoId), 'TODO 删除失败')
        this.todoEvents = this.todoEvents.filter(item => this.todoIdFromEvent(item) !== todoId)
        this.eventDialogVisible = false
        this.$message.success('TODO 已删除')
      } catch (error) {
        this.$message.error(error.message || 'TODO 删除失败')
      }
    },
    async deleteEvent () {
      if (this.eventForm.source_type === 'todo') {
        await this.deleteCalendarTodoEvent()
        return
      }
      if (!this.canEditEvent) {
        this.$message.info('当前日程不可编辑')
        return
      }
      const eventId = this.editingEventId
      this.events = this.events.filter(item => item.id !== this.editingEventId)
      this.persistEvents()

      if (this.backendAvailable) {
        try {
          const res = await deleteCalendarEvent(this.userId, eventId)
          if (res && res.data && res.data.status === 'fail') throw new Error(res.data.msg || 'Calendar backend delete failed')
        } catch (error) {
          this.backendAvailable = false
          this.$message.warning('后端日历接口暂不可用，已先从本机删除')
        }
      }

      this.eventDialogVisible = false
      this.$message.success('日程已删除')
    },
    syncCategoryColor () {
      const category = this.categories.find(item => item.name === this.eventForm.category)
      if (category) this.eventForm.color = category.color
    },
    datePartFromDateTime (value) {
      const match = String(value || '').trim().match(/^(\d{4}-\d{2}-\d{2})/)
      return match ? match[1] : ''
    },
    timePartFromDateTime (value) {
      const text = String(value || '').trim()
      const match = text.match(/[ T](\d{1,2}:\d{2})/) || text.match(/^(\d{1,2}:\d{2})$/)
      if (!match) return ''
      return `${match[1].split(':')[0].padStart(2, '0')}:${match[1].split(':')[1]}`
    },
    normalizeEvent (event) {
      const isTodo = event.source_type === 'todo'
      if (isTodo) {
        const todoId = this.todoIdFromEvent(event)
        const startDate = this.datePartFromDateTime(event.start_time) || event.date
        const endDate = this.datePartFromDateTime(event.end_time) || event.endDate
        const startTime = this.timePartFromDateTime(event.start_time) || this.timePartFromDateTime(event.startTime)
        const endTime = this.timePartFromDateTime(event.end_time) || this.timePartFromDateTime(event.endTime)
        const date = event.date || startDate || endDate
        const timeType = event.time_type || (event.start_time ? '时间段' : '截止时间')
        return {
          ...event,
          id: event.id || todoId,
          todo_id: event.todo_id || todoId,
          title: event.title || event.todo_content || '',
          date,
          endDate: timeType === '时间段' ? event.endDate || endDate || date : date,
          time_type: timeType,
          startTime: timeType === '时间段' ? startTime : '',
          endTime,
          allDay: timeType === '截止时间' ? !endTime : !startTime && !endTime,
          finish_status: this.isEventTodoFinished(event),
          category: 'TODO',
          color: event.color || '#7c5cff',
          notes: event.notes || event.todo_note || '',
          list_id: event.list_id || '',
          todo_labels: Array.isArray(event.todo_labels) ? event.todo_labels : [],
          participant_ids: Array.isArray(event.participant_ids) ? event.participant_ids : []
        }
      }
      return {
        ...event,
        endDate: event.endDate || event.date,
        participant_ids: Array.isArray(event.participant_ids) ? event.participant_ids : []
      }
    },
    eventEndDate (event) {
      if (event.source_type === 'todo') return event.endDate || event.date
      return event.allDay ? event.endDate || event.date : event.date
    },
    eventOccursOnDate (event, date) {
      return !dayjs(date).isBefore(dayjs(event.date), 'day') && !dayjs(date).isAfter(dayjs(this.eventEndDate(event)), 'day')
    },
    eventDates (event) {
      const start = dayjs(event.date)
      const end = dayjs(this.eventEndDate(event))
      const days = Math.max(end.diff(start, 'day'), 0) + 1
      return Array.from({ length: days }, (item, index) => start.add(index, 'day').format('YYYY-MM-DD'))
    },
    eventsForDate (date) {
      return this.calendarItems.filter(event => this.eventOccursOnDate(event, date)).sort(this.sortEvents)
    },
    allDayEventsForDate (date) {
      return this.eventsForDate(date).filter(event => event.allDay)
    },
    eventsForHour (date, hour) {
      return this.eventsForDate(date).filter(event => {
        if (event.allDay) return false
        const eventTime = event.startTime || event.endTime
        if (!eventTime) return false
        return Number(eventTime.split(':')[0]) === hour
      })
    },
    sortEvents (a, b) {
      if (a.date !== b.date) return a.date.localeCompare(b.date)
      if (a.allDay !== b.allDay) return a.allDay ? -1 : 1
      return (a.startTime || '').localeCompare(b.startTime || '')
    },
    eventTimeLabel (event) {
      if (event.source_type === 'todo' && event.allDay) return ''
      if (!event.allDay) {
        const endDate = event.endDate || event.date
        if (!event.startTime && !event.endTime) return ''
        if (!event.startTime && event.endTime) {
          if (endDate && endDate !== event.date) {
            return `截止 ${dayjs(endDate).format('M月D日')} ${event.endTime}`
          }
          return `截止 ${event.endTime}`
        }
        if (event.startTime && !event.endTime) return event.startTime
        if (endDate && endDate !== event.date) {
          return `${dayjs(event.date).format('M月D日')} ${event.startTime || ''} - ${dayjs(endDate).format('M月D日')} ${event.endTime || ''}`.trim()
        }
        return `${event.startTime || ''} - ${event.endTime || ''}`.trim()
      }
      const endDate = this.eventEndDate(event)
      if (!endDate || endDate === event.date) return '全天'
      return `全天 ${dayjs(event.date).format('M月D日')} - ${dayjs(endDate).format('M月D日')}`
    },
    eventParticipantCount (event) {
      return Array.isArray(event.participant_ids) ? event.participant_ids.length : 0
    },
    eventParticipantNames (event) {
      if (!Array.isArray(event.participant_ids) || event.participant_ids.length === 0) return ''
      const names = event.participant_ids.map(id => {
        const user = this.colleagues.find(item => item.id === id)
        return user ? user.name : ''
      }).filter(Boolean)
      return names.join('、')
    },
    formatDisplayDate (date) {
      return dayjs(date).format('YYYY年M月D日 dddd')
    }
  }
}
</script>

<style lang="scss" scoped>
.calendar-page {
  min-height: 100%;
  color: #303133;
}

.calendar-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
  padding: 16px 18px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

.calendar-title {
  font-size: 20px;
  font-weight: 600;
}

.calendar-subtitle {
  margin-top: 4px;
  color: #909399;
  font-size: 13px;
}

.mode-jump-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.mode-jump-button {
  min-width: 42px;
  height: 28px;
  padding: 0 10px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #606266;
  font-size: 13px;
  line-height: 26px;
  cursor: pointer;

  &.active {
    border-color: #409eff;
    background: #ecf5ff;
    color: #409eff;
    font-weight: 600;
  }
}

.calendar-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  .el-button + .el-button {
    margin-left: 0;
  }
}

.calendar-layout {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 16px;
}

.calendar-main,
.calendar-side {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

.calendar-main {
  min-width: 0;
  overflow: hidden;
}

.month-view {
  display: grid;
  grid-template-columns: repeat(7, minmax(96px, 1fr));
}

.week-head {
  padding: 12px;
  text-align: center;
  color: #606266;
  background: #f5f7fa;
  border-right: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
  font-weight: 600;
}

.month-cell {
  min-height: 122px;
  padding: 10px;
  border-right: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
  cursor: pointer;
  box-sizing: border-box;
}

.month-cell.muted {
  color: #c0c4cc;
  background: #fafafa;
}

.month-cell.today .cell-day,
.day-column-head.today strong {
  color: #fff;
  background: #4a8af4;
}

.month-cell.selected {
  box-shadow: inset 0 0 0 2px #4a8af4;
}

.cell-day-row {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.cell-day {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  font-weight: 600;
}

.day-mode-badge {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  max-width: 88px;
  height: 20px;
  padding: 0 6px;
  border: 1px solid #7c5cff;
  border-radius: 4px;
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: 18px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.week-mode-badge {
  margin-top: 6px;
}

.cell-events {
  margin-top: 8px;
}

.event-chip {
  min-width: 0;
  margin-top: 6px;
  padding: 5px 6px;
  border-left: 3px solid #4a8af4;
  border-radius: 4px;
  background: #f5f7fa;
  color: #303133;
  font-size: 12px;
}

.event-chip-time,
.side-event-time {
  color: #909399;
  font-size: 12px;
  line-height: 16px;
}

.event-chip-main {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  margin-top: 3px;
}

.event-category {
  flex-shrink: 0;
  padding: 0 4px;
  border: 1px solid #4a8af4;
  border-radius: 3px;
  font-size: 11px;
  line-height: 16px;
  white-space: nowrap;
}

.event-title {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.event-participants {
  flex-shrink: 0;
  color: #409eff;
}

.more-events {
  margin-top: 6px;
  color: #409eff;
  font-size: 12px;
}

.week-view {
  display: grid;
  grid-template-columns: 64px repeat(7, minmax(96px, 1fr));
}

.time-column,
.day-column-head,
.all-day-label,
.all-day-slot,
.time-label,
.time-slot {
  border-right: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
}

.day-column-head {
  padding: 10px;
  text-align: center;
  background: #f5f7fa;
  cursor: pointer;

  span {
    display: block;
    color: #909399;
    font-size: 12px;
  }

  strong {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    height: 24px;
    margin-top: 4px;
    border-radius: 12px;
  }
}

.time-label {
  padding: 10px 8px;
  color: #909399;
  font-size: 12px;
  text-align: right;
}

.all-day-label {
  padding: 12px 8px;
  color: #909399;
  font-size: 12px;
  text-align: right;
  background: #fbfcff;
}

.all-day-slot {
  min-height: 48px;
  padding: 5px;
  box-sizing: border-box;
  background: #fbfcff;
}

.time-slot {
  min-height: 54px;
  padding: 5px;
  box-sizing: border-box;
}

.week-event {
  margin-bottom: 4px;
  padding: 5px 6px;
  border-radius: 4px;
  color: #fff;
  font-size: 12px;
  cursor: pointer;
}

.all-day-event {
  font-weight: 600;
}

.day-view {
  padding: 8px 0;
}

.day-all-day-row,
.day-time-row {
  display: grid;
  grid-template-columns: 72px minmax(0, 1fr);
  min-height: 58px;
  border-bottom: 1px solid #ebeef5;
}

.day-all-day-row {
  background: #fbfcff;
}

.day-time-label {
  padding: 12px 10px;
  color: #909399;
  text-align: right;
  font-size: 12px;
}

.day-time-events {
  padding: 8px;
}

.all-day-list {
  min-height: 42px;
}

.day-event {
  margin-bottom: 8px;
  padding: 10px;
  border-left: 4px solid #4a8af4;
  border-radius: 4px;
  background: #f5f7fa;
  cursor: pointer;

  span,
  p {
    display: block;
    margin: 4px 0 0;
    color: #606266;
    font-size: 13px;
  }
}

.all-day-day-event {
  background: #fff;
}

.list-view {
  padding: 16px;
}

.list-group + .list-group {
  margin-top: 20px;
}

.list-date {
  margin-bottom: 8px;
  color: #606266;
  font-weight: 600;
}

.list-event {
  display: flex;
  gap: 10px;
  padding: 12px;
  border: 1px solid #ebeef5;
  border-radius: 6px;
  cursor: pointer;

  & + & {
    margin-top: 8px;
  }

  p {
    margin: 5px 0 0;
    color: #909399;
  }
}

.dot {
  flex: 0 0 10px;
  width: 10px;
  height: 10px;
  margin-top: 5px;
  border-radius: 50%;
}

.calendar-side {
  padding: 16px;
  align-self: start;
}

.side-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
}

.side-title {
  font-weight: 600;
}

.side-actions {
  display: flex;
  flex-shrink: 0;
  gap: 8px;
}

.day-mode-select {
  width: 170px;
  margin-top: 8px;
}

.side-event {
  display: flex;
  gap: 10px;
  padding: 12px 0;
  border-top: 1px solid #ebeef5;
  cursor: pointer;

  p {
    margin: 4px 0 0;
    color: #909399;
    font-size: 13px;
  }

  .side-event-time {
    margin-top: 0;
  }
}

.side-event-color {
  flex: 0 0 4px;
  border-radius: 2px;
}

.side-event-content {
  min-width: 0;
}

.side-event-main {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  margin-top: 3px;

  strong {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.time-pickers {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.date-range-picker {
  margin-top: 10px;
}

.readonly-alert {
  margin-bottom: 16px;
}

.participant-select {
  width: 100%;
}

.participant-option-meta {
  float: right;
  color: #909399;
  font-size: 12px;
}

@media (max-width: 1100px) {
  .calendar-toolbar,
  .calendar-layout {
    display: block;
  }

  .calendar-actions {
    margin-top: 12px;
  }

  .calendar-side {
    margin-top: 16px;
  }

  .month-view,
  .week-view {
    overflow-x: auto;
  }
}
</style>
